import { useCallback, useRef, useState } from 'react';
import { useLanguage } from '../lib/language';

export interface BeforeAfterSliderProps {
  beforeUrl: string;
  afterUrl: string;
  alt?: string;
  onClose: () => void;
}

/**
 * v7 — Before/After image slider. The parent image sits underneath and
 * the current image is rendered on top, clipped via `clip-path: inset`.
 * A vertical divider sits at the boundary; drag it to compare. Arrow
 * keys nudge 5%; Home/End jump to extremes; Esc closes.
 */
export function BeforeAfterSlider(props: BeforeAfterSliderProps) {
  const { beforeUrl, afterUrl, alt, onClose } = props;
  const { lang } = useLanguage();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [ratio, setRatio] = useState(0.5);
  const draggingRef = useRef(false);

  const updateFromClientX = useCallback((clientX: number) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const r = (clientX - rect.left) / rect.width;
    setRatio(Math.max(0, Math.min(1, r)));
  }, []);

  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      draggingRef.current = true;
      containerRef.current?.setPointerCapture(e.pointerId);
      updateFromClientX(e.clientX);
    },
    [updateFromClientX],
  );
  const onPointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!draggingRef.current) return;
      updateFromClientX(e.clientX);
    },
    [updateFromClientX],
  );
  const onPointerUp = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    draggingRef.current = false;
    containerRef.current?.releasePointerCapture(e.pointerId);
  }, []);

  const onKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') onClose();
    else if (e.key === 'ArrowLeft') { e.preventDefault(); setRatio((r) => Math.max(0, r - 0.05)); }
    else if (e.key === 'ArrowRight') { e.preventDefault(); setRatio((r) => Math.min(1, r + 0.05)); }
    else if (e.key === 'Home') { e.preventDefault(); setRatio(0); }
    else if (e.key === 'End') { e.preventDefault(); setRatio(1); }
  }, [onClose]);

  const beforeLabel = lang === 'plain' ? 'Before' : 'v(N)';
  const afterLabel = lang === 'plain' ? 'After' : 'v(N+1)';
  const hint = lang === 'plain' ? 'Drag the bar to compare' : 'Drag divider to reveal diff';
  const title = lang === 'plain' ? 'Compare with previous' : 'Diff with parent';

  return (
    <div
      data-testid="before-after-slider"
      className="fixed inset-0 z-40 flex items-center justify-center bg-neutral-950/80 p-4 backdrop-blur"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onKeyDown={onKeyDown}
      tabIndex={-1}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="relative w-full max-w-5xl">
        <div className="mb-2 flex items-center justify-between text-xs text-neutral-100">
          <span className="font-semibold uppercase tracking-wide">{title}</span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close compare"
            className="rounded-md border border-white/20 bg-black/40 px-3 py-1 text-xs text-white hover:bg-black/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          >
            Close
          </button>
        </div>
        <div
          ref={containerRef}
          className="relative aspect-[16/10] w-full select-none overflow-hidden rounded-xl border border-neutral-800 bg-neutral-900 touch-none"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          aria-label="Before / after slider — drag to compare"
          role="slider"
          aria-valuenow={Math.round(ratio * 100)}
          aria-valuemin={0}
          aria-valuemax={100}
          tabIndex={0}
        >
          <img src={beforeUrl} alt={alt ?? beforeLabel} className="absolute inset-0 h-full w-full object-cover" draggable={false} />
          <img src={afterUrl} alt={alt ?? afterLabel} className="absolute inset-0 h-full w-full object-cover" draggable={false} style={{ clipPath: `inset(0 ${(1 - ratio) * 100}% 0 0)` }} />
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-y-0"
            style={{ left: `${ratio * 100}%`, width: 2, background: 'rgba(255,255,255,0.85)', transform: 'translateX(-1px)', boxShadow: '0 0 0 1px rgba(0,0,0,0.4)' }}
          >
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border border-white/80 bg-white/95 px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-neutral-900 shadow-md">
              {hint}
            </div>
          </div>
          <span className="pointer-events-none absolute left-2 top-2 rounded-md bg-black/60 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white" data-testid="compare-label-before">{beforeLabel}</span>
          <span className="pointer-events-none absolute right-2 top-2 rounded-md bg-black/60 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white" data-testid="compare-label-after">{afterLabel}</span>
        </div>
      </div>
    </div>
  );
}
